/**
 * 画面遷移中の上端進捗バー（PRODUCT_DEV-703）。
 *
 * router.tsx の各ページを `React.lazy` + `Suspense` から react-router の
 * `route.lazy` へ移したことで、チャンク解決中も**現在の画面が出たまま**になった
 * （以前は全画面 Splash = `min-h-screen bg-white` でタブバーごと白くなっていた）。
 * 代わりにフィードバックが無くなるので、その間だけ上端に細いバーを出す。
 *
 * - **150ms 以上かかったときだけ出す**。即時表示にすると、プリキャッシュ済みで
 *   一瞬で終わる通常の遷移でも毎回チラつく
 * - 幅ではなく `transform` で動かす（`animation: navProgress` / tailwind.config.js）。
 *   遷移の待ちの実体はチャンクのパース/評価によるメインスレッド阻害なので、
 *   width アニメーションだと「バーが出たまま静止」して逆に固まって見える
 * - `EnvironmentBanner`（`fixed top-0 h-[3px] z-[9999]`）**より上**に重ねる。
 *   下に置くと dev/stg では環境バナーに完全に隠れて見えない
 */
import { useEffect, useState } from "react";
import { useNavigation } from "react-router";

/** これ未満で終わる遷移にはバーを出さない（チラつき防止）。 */
export const NAV_PROGRESS_DELAY_MS = 150;

export const NavigationProgress: React.FC = () => {
  const navigation = useNavigation();
  // "loading"（遷移 / 再検証）と "submitting" の両方を待ちとして扱う
  const isBusy = navigation.state !== "idle";
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isBusy) return;
    const timer = window.setTimeout(
      () => setVisible(true),
      NAV_PROGRESS_DELAY_MS,
    );
    // 非表示への戻しは cleanup で行う（effect 本体で setState すると
    // react-hooks/set-state-in-effect に掛かる。遷移完了 = isBusy が false に
    // なった時点で cleanup が走るので、タイミングとしても等価）。
    return () => {
      window.clearTimeout(timer);
      setVisible(false);
    };
  }, [isBusy]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 h-[3px] overflow-hidden z-[10000]"
      role="progressbar"
      aria-label="画面を読み込み中"
      aria-busy="true"
    >
      <div className="h-full w-1/4 bg-primary animate-navProgress motion-reduce:w-full motion-reduce:animate-none" />
    </div>
  );
};
